import React, { useState, useEffect } from 'react';

import type { Image, Category } from '../../services/api';
import api from '../../services/api';

import styles from './ImageEditModal.module.css';

/**
 * ImageEditModal Props
 */
type ImageEditModalProps = {
  readonly image: Image;
  readonly onSave: (updatedData: Partial<Image>) => Promise<void>;
  readonly onClose: () => void;
};

/**
 * ImageEditModal Component
 *
 * Modal dialog for editing image metadata including title, description, category, and order.
 *
 * Features:
 * - Form for editing image metadata
 * - Image preview
 * - Save and cancel buttons
 * - Keyboard navigation (Escape to close)
 * - Click outside to close
 * - Loading state during save
 * - Responsive mobile design
 * @param root0
 * @param root0.image
 * @param root0.onSave
 * @param root0.onClose
 */
const ImageEditModal: React.FC<ImageEditModalProps> = ({ image, onSave, onClose }) => {
  // Form state
  const [formData, setFormData] = useState({
    title: image.title ?? '',
    description: image.description ?? '',
    category: image.category ?? '',
    order: image.order ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<ReadonlyArray<Category>>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  /**
   * Handle form field changes
   * @param field
   * @param value
   */
  const handleChange = (field: string, value: string | number): void => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Handle form submission
   * @param e
   */
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);

    try {
      await onSave(formData);
      // onSave will handle closing the modal and showing success toast
    } catch (error) {
      console.error('Save error:', error);
      // Error toast will be shown by parent component
    } finally {
      setSaving(false);
    }
  };

  /**
   * Load all categories on mount
   */
  useEffect(() => {
    const loadCategories = async (): Promise<void> => {
      try {
        const response = await api.categories.getAll();
        setCategories(response.categories);
      } catch (error) {
        console.error('Failed to load categories:', error);
        // Continue with empty categories - user can still edit other fields
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);

  /**
   * Handle backdrop click (close modal)
   * @param e
   */
  const handleBackdropClick = (e: React.MouseEvent): void => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  /**
   * Handle Escape key to close modal
   */
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return (): void => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  /**
   * Prevent body scroll when modal is open
   */
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return (): void => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div
      className={styles.modalBackdrop}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className={styles.modal}>
        {/* Modal Header */}
        <div className={styles.modalHeader}>
          <h3 id="modal-title">Edit Image</h3>
          <button
            onClick={onClose}
            className={styles.closeButton}
            aria-label="Close modal"
            type="button"
          >
            ×
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className={styles.modalBody}>
          {/* Image Preview */}
          <div className={styles.imagePreview}>
            <img src={image.mediumUrl ?? image.url} alt={image.description ?? image.originalName} />
          </div>

          {/* Title Field */}
          <div className={styles.formGroup}>
            <label htmlFor="title">Title</label>
            <input
              id="title"
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Enter image title"
              maxLength={200}
            />
            <span className={styles.charCount}>{formData.title.length}/200</span>
          </div>

          {/* Description/Alt Text Field */}
          <div className={styles.formGroup}>
            <label htmlFor="description">
              Description <span className={styles.optional}>(for accessibility)</span>
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe this image for screen readers"
              rows={3}
              maxLength={500}
            />
            <span className={styles.charCount}>{formData.description.length}/500</span>
          </div>

          {/* Category Field */}
          <div className={styles.formGroup}>
            <label htmlFor="category">Category</label>
            <select
              id="category"
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              disabled={loadingCategories}
            >
              <option value="">No Category</option>
              {loadingCategories ? (
                <option disabled>Loading categories...</option>
              ) : (
                <>
                  {/* Group categories by type */}
                  {['portfolio', 'shop', 'courses'].map((type) => {
                    const typedCategories = categories.filter((cat) => cat.type === type);
                    if (typedCategories.length === 0) return null;

                    return (
                      <optgroup key={type} label={type.charAt(0).toUpperCase() + type.slice(1)}>
                        {typedCategories
                          .filter((cat) => cat.enabled)
                          .sort((a, b) => a.order - b.order)
                          .map((cat) => (
                            <option key={cat._id} value={cat.slug}>
                              {cat.name}
                            </option>
                          ))}
                      </optgroup>
                    );
                  })}
                </>
              )}
            </select>
          </div>

          {/* Order Field */}
          <div className={styles.formGroup}>
            <label htmlFor="order">
              Display Order <span className={styles.optional}>(for sorting)</span>
            </label>
            <input
              id="order"
              type="number"
              value={formData.order}
              onChange={(e) => handleChange('order', parseInt(e.target.value) || 0)}
              min="0"
              step="1"
            />
          </div>

          {/* Modal Footer */}
          <div className={styles.modalFooter}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelButton}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" disabled={saving} className={styles.saveButton}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ImageEditModal;
